import {API_ENDPOINTS, LoadingKeys} from '@/constants';
import {
  BannerType,
  EnabledStatus,
  IBannerCreateOrEditPayload,
  IBannerItem,
  ICoachBannerCreateOrEditPayload,
  ICreateOrUpdateItem,
  ListResponse,
} from '@/models';
import {changeEnabledStatus, http, updateItemById} from '@/utils';

import {AxiosRequestConfig} from 'axios';
import {create} from 'zustand';
import {handleApiCallInStore} from './utils';
import {persist} from 'zustand/middleware';
import {t} from 'i18next';

export interface IBannerStore {
  coachBanners: IBannerItem[];
  coachBannersNbHits: number;
  learnerBannersNbHits: number;
  bannerDetails: IBannerItem;
  learnerBanners: IBannerItem[];
  getBannersList: (params, onSuccess?: (items: IBannerItem[]) => void) => void;
  changeBannerStatus: (payload: EnabledStatus, bannerType: BannerType) => void;
  getBannerById: (id: string, onSuccess: (data: IBannerItem) => void) => void;
  createOrUpdateLearnerBanner: (
    data: ICreateOrUpdateItem<IBannerCreateOrEditPayload>
  ) => void;
  createOrUpdateCoachBanner: (
    data: ICreateOrUpdateItem<ICoachBannerCreateOrEditPayload>
  ) => void;
  sortBanners: (
    payload: { banners: string[]; role: 'learner' | 'coach' },
    onSuccess: () => void
  ) => void;
}

export const useBannerStore = create(
  persist<IBannerStore>(
    set => ({
      coachBanners: [],
      bannerDetails: undefined,
      coachBannersNbHits: 0,
      learnerBannersNbHits: 0,
      learnerBanners: [],
      sortBanners: (payload, onSuccess) => {
        handleApiCallInStore(LoadingKeys.SortBanners, async () => {
          await http.put<{ banners: string[] }>(
            API_ENDPOINTS.banners.sort,
            payload
          );
          await onSuccess();
        });
      },
      createOrUpdateLearnerBanner: ({id, payload, onSuccess}) => {
        handleApiCallInStore(
          LoadingKeys.CreateOrEditLearnerBanner,
          async () => {
            const apiLink = `${API_ENDPOINTS.banners.all}${id ? `/${id}` : ''}`;
            if (id) {
              await http.put(apiLink, payload);
            } else {
              await http.post(apiLink, payload);
            }
            await onSuccess();
          }
        );
      },
      createOrUpdateCoachBanner: ({id, payload, onSuccess}) => {
        handleApiCallInStore(LoadingKeys.CreateOrEditCoachBanner, async () => {
          const apiLink = `${API_ENDPOINTS.banners.all}${id ? `/${id}` : ''}`;
          if (id) {
            await http.put(apiLink, payload);
          } else {
            await http.post(apiLink, payload);
          }
          await onSuccess();
        });
      },
      getBannerById: (id, onSuccess) => {
        handleApiCallInStore(LoadingKeys.GetBannerDetailsById, async () => {
          const {data} = await http.get<IBannerItem>(
            `${API_ENDPOINTS.banners.all}/${id}`
          );
          set(() => ({
            bannerDetails: data,
          }));
          onSuccess?.(data);
        });
      },
      changeBannerStatus: (payload, bannerType) => {
        handleApiCallInStore(LoadingKeys.ChangeBannerStatus, async () => {
          const {id, isEnabled} = payload;
          await http.put(`${API_ENDPOINTS.banners.all}/${id}/deactivate`, {
            isEnabled,
          });
          if (bannerType === 'coach') {
            set(({coachBanners}) => ({
              coachBanners: updateItemById(
                coachBanners,
                'isEnabled',
                isEnabled,
                id
              ),
            }));
          } else {
            set(({learnerBanners}) => ({
              learnerBanners: updateItemById(
                learnerBanners,
                'isEnabled',
                isEnabled,
                id
              ),
            }));
          }

          changeEnabledStatus(isEnabled, t('banner'));
        });
      },
      getBannersList: async (params, onSuccess) => {
        handleApiCallInStore(LoadingKeys.GetBannersList, async () => {
          const {data} = await http.get<ListResponse<IBannerItem>>(
            API_ENDPOINTS.banners.all,
            {params} as AxiosRequestConfig
          );
          const {items, nbHits} = data;
          if (onSuccess) {
            onSuccess(items);
          }
          if (params.role === 'coach') {
            set(() => ({
              coachBanners: items ?? [],
              coachBannersNbHits: nbHits ?? 0,
            }));
          } else {
            set(() => ({
              learnerBanners: items ?? [],
              learnerBannersNbHits: nbHits ?? 0,
            }));
          }
        });
      },
    }),
    {
      name: 'banner-storage',

    }
  )
);
